import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, usePermissions } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePermission?: keyof ReturnType<typeof usePermissions>;
  fallbackPath?: string;
  showError?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requirePermission,
  fallbackPath = '/dashboard',
  showError = true
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const permissions = usePermissions();

  // Still loading auth state
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check specific permission if required
  if (requirePermission) {
    const hasPermission = permissions[requirePermission];
    
    if (typeof hasPermission === 'boolean' && !hasPermission) {
      if (showError) {
        return (
          <div className="error-state">
            <h2>Access Denied</h2>
            <p>You don't have permission to access this page.</p>
            <button onClick={() => window.history.back()}>
              Go Back
            </button>
          </div>
        );
      } else {
        return <Navigate to={fallbackPath} replace />;
      }
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
